export const DIGITAL_HUMAN = "digital_human";
export const ADMIN = "admin";
export const USER_ADMIN = "user_admin";
export const USERS = "users";
export const VIDEO_USERS = "video_users";
export const BRI_USERS = "bri_users";
export const BRI_ADMIN = "bri_admin";

/**
 * The Cognito pool that issued a verified identity token. Pool scope is an
 * authorization boundary: groups can narrow access within a pool, but cannot
 * grant access to another pool's content.
 */
export type UserPoolScope = "lote" | "steyvers";

export interface AuthSubject {
  groups?: string[];
  userPool?: UserPoolScope;
}

export const POOL_RESTRICTED_CATEGORIES = new Set(["Steyvers", "Abdou"]);
const ADMIN_ONLY_PERSONA_CATEGORIES = new Set(["Abdou"]);

export const isAnyAdmin = (groups?: string[]) => {
  if (!groups) return false;
  return groups.includes(ADMIN) || groups.includes(USER_ADMIN) || groups.includes(BRI_ADMIN);
};

export const isUserAdmin = (groups?: string[]) => {
  if (!groups) return false;
  return groups.includes(USER_ADMIN) || groups.includes(BRI_ADMIN);
};

export const isUserAuthorized = (groups?: string[]) => {
  if (!groups) return false;
  return (
    groups.includes(DIGITAL_HUMAN) ||
    groups.includes(ADMIN) ||
    groups.includes(USER_ADMIN) ||
    groups.includes(USERS) ||
    groups.includes(VIDEO_USERS) ||
    groups.includes(BRI_USERS) ||
    groups.includes(BRI_ADMIN)
  );
};

export const isLincolnAuthorized = (groups?: string[]) => {
  if (!groups) return false;
  return (
    groups.includes(ADMIN) ||
    groups.includes(BRI_USERS) ||
    groups.includes(BRI_ADMIN)
  );
};

export const isMainSiteAuthorized = (groups?: string[]) => {
  if (!groups) return false;
  return (
    groups.includes(DIGITAL_HUMAN) ||
    groups.includes(ADMIN) ||
    groups.includes(USER_ADMIN) ||
    groups.includes(USERS) ||
    groups.includes(VIDEO_USERS)
  );
};

export const isPrinceAuthorized = (groups?: string[]) => {
  if (!groups) return false;
  return (
    groups.includes(DIGITAL_HUMAN) ||
    groups.includes(ADMIN) ||
    groups.includes(USER_ADMIN) ||
    groups.includes(USERS) ||
    groups.includes(VIDEO_USERS)
  );
};

export const isSuperAdmin = (groups?: string[]) => {
  if (!groups) return false;
  return groups.includes(ADMIN);
};

/**
 * Groups that can view multi-turn conversation results and review persona replies.
 * bri_users is deliberately excluded: BRI end users must not review (decided 2026-10-07).
 * Uploading and deleting result sets stays super-admin only (isSuperAdmin).
 */
export const CONVERSATION_REVIEW_GROUPS = [ADMIN, USER_ADMIN, BRI_ADMIN];

export const canReviewConversationResults = (groups?: string[]) =>
  groups?.some((g) => CONVERSATION_REVIEW_GROUPS.includes(g)) ?? false;

export const canAccessCall = (groups?: string[]) => {
  if (!groups) return false;
  // All groups except 'users' have access to call
  return (
    groups.includes(DIGITAL_HUMAN) ||
    groups.includes(ADMIN) ||
    groups.includes(USER_ADMIN) ||
    groups.includes(VIDEO_USERS) ||
    groups.includes(BRI_USERS) ||
    groups.includes(BRI_ADMIN)
  );
};

/**
 * Fail-closed allowlist: only digital_human, admin, user_admin, bri_users and
 * bri_admin see the detailed agentic workflow trace. Everyone else — base
 * learner/test accounts (`users`, `video_users`), unknown groups, or no
 * groups — gets the abstracted "processing" animation. The API enforces the
 * same check when streaming and returning chat history.
 */
export const canViewDetailedAgenticWorkflow = (groups?: string[]) => {
  if (!groups) return false;
  return (
    groups.includes(DIGITAL_HUMAN) ||
    groups.includes(ADMIN) ||
    groups.includes(USER_ADMIN) ||
    groups.includes(BRI_USERS) ||
    groups.includes(BRI_ADMIN)
  );
};

export const isRestrictedUser = (groups?: string[]) => {
  if (!groups) return false;
  // Admin and digital_human have full access
  if (groups.includes(ADMIN) || groups.includes(DIGITAL_HUMAN)) {
    return false;
  }
  // user_admin, users, video_users, bri_users, and bri_admin have restricted (non-super-admin) persona access
  return (
    groups.includes(USER_ADMIN) ||
    groups.includes(USERS) ||
    groups.includes(VIDEO_USERS) ||
    groups.includes(BRI_USERS) ||
    groups.includes(BRI_ADMIN)
  );
};

const isCategoryAllowedForPool = (category: string | undefined, userPool: UserPoolScope | undefined) => {
  if (userPool === "lote") {
    return category !== "Steyvers";
  }
  return userPool === "steyvers" && category === "Steyvers";
};

export const canAccessPersona = (persona: { category?: string; adminOnly?: boolean }, subject?: AuthSubject) => {
  const groups = subject?.groups;
  if (!isCategoryAllowedForPool(persona.category, subject?.userPool)) return false;
  if (ADMIN_ONLY_PERSONA_CATEGORIES.has(persona.category ?? "")) {
    return groups?.includes(ADMIN) ?? false;
  }
  if (!isUserAuthorized(groups)) return false;
  if (persona.adminOnly && !groups?.includes(ADMIN)) return false;
  if (!isRestrictedUser(groups)) return true;
  if (groups?.includes(USER_ADMIN)) {
    return true;
  }
  if (groups?.includes(BRI_USERS) || groups?.includes(BRI_ADMIN)) {
    return persona.category === "BRI";
  }
  if (groups?.includes(USERS) || groups?.includes(VIDEO_USERS)) {
    return persona.category === "LOTE" || persona.category === "Steyvers";
  }
  return false;
};

export const assertPersonaPoolAccess = (
  persona: { category?: string; adminOnly?: boolean },
  subject: AuthSubject | null,
) => subject
  ? canAccessPersona(persona, subject)
  : !persona.adminOnly && !POOL_RESTRICTED_CATEGORIES.has(persona.category ?? "");
